/* ============================================================================
 * Peter and May - Header behaviors
 * Web components : <pm-header>, <pm-dropdown>, <pm-mobile-drawer>
 * Source : docs/components.md (Menu Bar, Dropdowns, Mobile Drawer)
 * ============================================================================ */

(function () {
  'use strict';

  /* ---------------------------------------------------------------------------
   * Scrollbar width - exposed as --scrollbar-w so the CSS scroll-locks
   * (dropdown / mobile drawer) can pad the body and avoid a layout shift
   * when the scrollbar disappears.
   * --------------------------------------------------------------------------*/
  function setScrollbarWidth() {
    // Skip while a lock is active (the scrollbar is hidden, width would read 0)
    if (getComputedStyle(document.body).overflow === 'hidden') return;
    const w = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty('--scrollbar-w', w + 'px');
  }
  setScrollbarWidth();
  window.addEventListener('resize', setScrollbarWidth);

  /* ---------------------------------------------------------------------------
   * <pm-header> - always-visible sticky header (position:sticky via CSS).
   *   Element kept as a custom element so dropdowns can sync via closest().
   * --------------------------------------------------------------------------*/
  class PmHeader extends HTMLElement {}
  customElements.define('pm-header', PmHeader);

  /* ---------------------------------------------------------------------------
   * <pm-dropdown> - hover + click toggle for header dropdowns
   *   - Hovering the trigger opens
   *   - Clicking the trigger also navigates (link kept active)
   *   - Mouse out (with delay) closes
   *   - Escape closes
   *   - Outside click closes
   *   - Syncs [data-dropdown-open] on parent <pm-header>
   * --------------------------------------------------------------------------*/
  const CLOSE_DELAY = 280;
  let openDropdown = null;

  class PmDropdown extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('[data-dropdown-trigger]');
      this.panel = this.querySelector('.pm-dropdown__panel');
      if (!this.trigger || !this.panel) return;
      this._bind();
    }
    disconnectedCallback() {
      this._unbind();
      if (openDropdown === this) openDropdown = null;
    }

    _bind() {
      this._onTriggerEnter = this._onTriggerEnter.bind(this);
      this._onPanelEnter = this._onPanelEnter.bind(this);
      this._onMouseLeave = this._onMouseLeave.bind(this);
      this._onKeyDown = this._onKeyDown.bind(this);

      this.addEventListener('mouseenter', this._onTriggerEnter);
      this.addEventListener('mouseleave', this._onMouseLeave);
      this.panel.addEventListener('mouseenter', this._onPanelEnter);
      document.addEventListener('keydown', this._onKeyDown);

      // Focus reveal (keyboard nav)
      this.trigger.addEventListener('focus', this._onTriggerEnter);
    }
    _unbind() {
      this.removeEventListener('mouseenter', this._onTriggerEnter);
      this.removeEventListener('mouseleave', this._onMouseLeave);
      this.panel.removeEventListener('mouseenter', this._onPanelEnter);
      document.removeEventListener('keydown', this._onKeyDown);
    }

    open() {
      clearTimeout(this._closeTimer);
      if (openDropdown && openDropdown !== this) openDropdown.close();
      // Align the panel's link list under its trigger (panel spans the full width)
      this.panel.style.setProperty('--pm-trigger-x', Math.round(this.trigger.getBoundingClientRect().left) + 'px');
      this.dataset.open = 'true';
      this.panel.removeAttribute('hidden');
      openDropdown = this;
      this._syncHeader(true);
    }
    close() {
      if (this.dataset.open !== 'true') return;
      delete this.dataset.open;
      // Wait for transition before hiding (so a11y tree stays clean)
      setTimeout(() => {
        if (this.dataset.open !== 'true') this.panel.setAttribute('hidden', '');
      }, 300);
      if (openDropdown === this) openDropdown = null;
      this._syncHeader(false);
    }

    _syncHeader(isOpen) {
      const header = this.closest('pm-header');
      if (!header) return;
      // Defer slightly to allow another dropdown to claim if hover transitions between them
      requestAnimationFrame(() => {
        const stillOpen = header.querySelector("pm-dropdown[data-open='true']");
        if (stillOpen) header.dataset.dropdownOpen = 'true';
        else delete header.dataset.dropdownOpen;
      });
    }

    _onTriggerEnter() { this.open(); }
    _onPanelEnter() { clearTimeout(this._closeTimer); }
    _onMouseLeave() {
      clearTimeout(this._closeTimer);
      this._closeTimer = setTimeout(() => this.close(), CLOSE_DELAY);
    }
    _onKeyDown(e) {
      if (e.key === 'Escape' && this.dataset.open === 'true') this.close();
    }
  }
  customElements.define('pm-dropdown', PmDropdown);

  /* ---------------------------------------------------------------------------
   * <pm-mobile-drawer> - open/close + focus trap + escape + body lock
   * --------------------------------------------------------------------------*/
  class PmMobileDrawer extends HTMLElement {
    connectedCallback() {
      this._bind();
    }
    disconnectedCallback() { this._unbind(); }

    _bind() {
      this._onKeyDown = this._onKeyDown.bind(this);
      this._onCloseClick = this._onCloseClick.bind(this);
      this._onTriggerClick = this._onTriggerClick.bind(this);
      this._onTabClick = this._onTabClick.bind(this);

      // Top-level row : one panel open at a time. A top-level item with no
      // sub-items is a plain link, so it carries no [data-tab] and is skipped.
      this._tabs = Array.from(this.querySelectorAll('[data-tab]'));
      this._tabs.forEach((tab) => tab.addEventListener('click', this._onTabClick));

      // Close button(s)
      this.querySelectorAll('[data-drawer-close]').forEach((btn) => {
        btn.addEventListener('click', this._onCloseClick);
      });
      document.addEventListener('keydown', this._onKeyDown);

      // Trigger button (in header)
      this._trigger = document.querySelector(`[aria-controls='${this.id}']`);
      if (this._trigger) this._trigger.addEventListener('click', this._onTriggerClick);
    }
    _unbind() {
      this.querySelectorAll('[data-drawer-close]').forEach((btn) => {
        btn.removeEventListener('click', this._onCloseClick);
      });
      (this._tabs || []).forEach((tab) => tab.removeEventListener('click', this._onTabClick));
      document.removeEventListener('keydown', this._onKeyDown);
      if (this._trigger) this._trigger.removeEventListener('click', this._onTriggerClick);
    }

    open() {
      this.removeAttribute('hidden');
      // double-rAF so transition kicks in
      requestAnimationFrame(() => requestAnimationFrame(() => {
        this.dataset.open = 'true';
      }));
      if (this._trigger) this._trigger.setAttribute('aria-expanded', 'true');
      this._lastFocus = document.activeElement;
      // Focus first focusable
      const f = this.querySelector('a, button, select, [tabindex]:not([tabindex="-1"])');
      if (f) f.focus();
    }
    close() {
      delete this.dataset.open;
      if (this._trigger) {
        this._trigger.setAttribute('aria-expanded', 'false');
        this._trigger.focus();
      }
      setTimeout(() => {
        if (this.dataset.open !== 'true') this.setAttribute('hidden', '');
      }, 300);
    }
    toggle() {
      if (this.dataset.open === 'true') this.close();
      else this.open();
    }

    _onTabClick(e) {
      const tab = e.currentTarget;
      const key = tab.dataset.tab;
      this._tabs.forEach((t) => {
        const on = t === tab;
        t.dataset.active = on ? 'true' : 'false';
        t.setAttribute('aria-expanded', on ? 'true' : 'false');
      });
      this.querySelectorAll('[data-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.panel !== key;
      });
      // Keep the chosen tab in view in the scroller. block:'nearest' so a tab at
      // the far end never drags the drawer's vertical scroll with it.
      tab.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }

    _onCloseClick(e) { e.preventDefault(); this.close(); }
    _onTriggerClick(e) { e.preventDefault(); this.toggle(); }
    _onKeyDown(e) {
      if (this.dataset.open !== 'true') return;
      if (e.key === 'Escape') this.close();
    }
  }
  customElements.define('pm-mobile-drawer', PmMobileDrawer);

  /* ---------------------------------------------------------------------------
   * Paris time - shared label builder
   *   "Paris : HH:MM", 24h, single space around the colon (per Figma). Used by
   *   the hero's sticky X on desktop and by <pm-paris-time> in the footer on
   *   mobile, where that X is hidden.
   * --------------------------------------------------------------------------*/
  function parisTimeLabel(tz) {
    const now = new Date();
    let formatted;
    try {
      formatted = new Intl.DateTimeFormat('fr-FR', {
        timeZone: tz || 'Europe/Paris',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }).format(now);
    } catch (e) {
      formatted = now.toTimeString().slice(0, 5);
    }
    return 'Paris : ' + formatted.replace('.', ':'); // some locales use a period
  }

  /* ---------------------------------------------------------------------------
   * <pm-paris-time> - the same time as a plain line, no button, no toggle
   *   Footer, mobile only : the hero's sticky X carries the clock on desktop,
   *   and it is hidden below 750px, so the footer picks the time back up. The
   *   element ships "Paris" as its server-rendered content, so a visitor with
   *   no JS still reads the city rather than an empty line.
   * --------------------------------------------------------------------------*/
  class PmParisTime extends HTMLElement {
    connectedCallback() {
      this.tz = this.dataset.parisTz || 'Europe/Paris';
      this._tick = this._tick.bind(this);
      this._tick();
      this._interval = setInterval(this._tick, 30000); // update every 30s
    }
    disconnectedCallback() {
      if (this._interval) clearInterval(this._interval);
    }
    _tick() {
      this.textContent = parisTimeLabel(this.tz);
    }
  }
  customElements.define('pm-paris-time', PmParisTime);

  /* ---------------------------------------------------------------------------
   * <pm-footer-menus> - footer menu columns, dropdowns on mobile only
   *   The columns ship open (sections/footer.liquid) so a visitor with no JS
   *   reads every link. Below 750px they are collapsed into <details> dropdowns
   *   - the stacked lists were the tallest thing in the footer. Back on desktop
   *   they are re-opened and pinned : the summary is inert there (CSS drops the
   *   pointer, this guard covers the keyboard).
   * --------------------------------------------------------------------------*/
  class PmFooterMenus extends HTMLElement {
    connectedCallback() {
      this._mq = window.matchMedia('(max-width: 749px)');
      this._sync = this._sync.bind(this);
      this._onToggle = this._onToggle.bind(this);
      this._sync();
      this.addEventListener('toggle', this._onToggle, true);
      if (this._mq.addEventListener) this._mq.addEventListener('change', this._sync);
      else this._mq.addListener(this._sync); // Safari < 14
    }
    disconnectedCallback() {
      this.removeEventListener('toggle', this._onToggle, true);
      if (this._mq.removeEventListener) this._mq.removeEventListener('change', this._sync);
      else this._mq.removeListener(this._sync);
    }
    _rows() {
      return this.querySelectorAll('details.pm-footer__menu-col');
    }
    _sync() {
      const collapsed = this._mq.matches;
      this._rows().forEach((row) => {
        row.open = !collapsed;
      });
    }
    _onToggle(event) {
      if (this._mq.matches) return;
      const row = event.target;
      if (row instanceof HTMLDetailsElement && !row.open) row.open = true;
    }
  }
  customElements.define('pm-footer-menus', PmFooterMenus);

  /* ---------------------------------------------------------------------------
   * <pm-spotify> - click-to-play facade for the footer player
   *   The footer ships on every page of the store, and the Spotify embed costs
   *   ~1MB of third-party JS plus its own cookies. So the server renders a
   *   button only, and the iframe is built here on the first click : one user
   *   gesture buys both the payload and the consent for those cookies.
   *   No JS at all : the <noscript> link opens the playlist on Spotify.
   * --------------------------------------------------------------------------*/
  class PmSpotify extends HTMLElement {
    connectedCallback() {
      this._button = this.querySelector('[data-pm-spotify-play]');
      if (!this._button || !this.dataset.embed) return;
      this._mount = this._mount.bind(this);
      this._button.addEventListener('click', this._mount, { once: true });
    }
    disconnectedCallback() {
      if (this._button && this._mount) this._button.removeEventListener('click', this._mount);
    }
    _mount() {
      const frame = document.createElement('iframe');
      frame.src = this.dataset.embed;
      frame.title = this.dataset.title || 'Spotify';
      frame.height = this.dataset.height || '152';
      frame.loading = 'lazy';
      frame.setAttribute('frameborder', '0');
      frame.setAttribute('allow', 'autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture');
      // Reserve the height before the swap so the footer does not jump under
      // the visitor while the embed boots.
      this.style.minHeight = frame.height + 'px';
      this.innerHTML = '';
      this.appendChild(frame);
      this.setAttribute('data-loaded', '');
    }
  }
  customElements.define('pm-spotify', PmSpotify);

  /* ---------------------------------------------------------------------------
   * <pm-home-hero> - Paris Time toggle (sticky X click)
   *   Toggles the visible "Paris : HH:MM" label next to the X mark.
   *   Format : 24h "HH : MM" with single space around colon (per Figma).
   *   Also carries the auto-slide of the split hero's mobile slider (opt-in,
   *   data-autoplay) - the swipe itself is pure CSS scroll-snap.
   * --------------------------------------------------------------------------*/
  class PmHomeHero extends HTMLElement {
    connectedCallback() {
      this._upgradeDesktopPane();
      this._initAutoSlide();
      this.tz = this.dataset.parisTz || 'Europe/Paris';
      this.toggleBtn = this.querySelector('[data-paris-toggle]');
      this.label = this.querySelector('[data-paris-label]');
      if (!this.toggleBtn || !this.label) return;
      // Localized aria labels (Liquid `| t` → data attributes)
      this.labelHide = this.toggleBtn.dataset.labelHide || 'Hide Paris time';
      this.labelShow = this.toggleBtn.dataset.labelShow || 'Show Paris time';
      this._onClick = this._onClick.bind(this);
      this._tick = this._tick.bind(this);
      this.toggleBtn.addEventListener('click', this._onClick);
      // Shown by default : Paris + current time visible on load
      this._show();
    }
    disconnectedCallback() {
      if (this.toggleBtn) this.toggleBtn.removeEventListener('click', this._onClick);
      if (this._interval) clearInterval(this._interval);
      this._teardownAutoSlide();
    }

    /* --- Split hero : the right pane is not lazy, and both halves land together ---
       The section already does this inline, before the first layout ; this is
       the theme editor path, where a re-rendered section's inline <script> is
       never executed. No-op on the front, the image is eager and the track is
       already flagged by then. */
    _upgradeDesktopPane() {
      if (!window.matchMedia('(min-width: 750px)').matches) return;
      const img = this.querySelector('picture[data-desktop-eager] img');
      if (img && img.loading === 'lazy') {
        img.setAttribute('fetchpriority', 'high');
        img.loading = 'eager';
      }
      const track = this.querySelector('[data-hero-track]');
      if (!track || track.hasAttribute('data-sync-reveal')) return;
      const imgs = track.querySelectorAll('img');
      if (imgs.length < 2) return;
      // Held unpainted until both crops are in : the heavier one must not pop
      // next to a half already on screen. Same guards as the inline script.
      track.setAttribute('data-sync-reveal', '');
      const reveal = () => track.setAttribute('data-ready', '');
      Promise.all(
        Array.from(imgs, (el) =>
          el.complete
            ? Promise.resolve()
            : new Promise((done) => {
                el.addEventListener('load', done, { once: true });
                el.addEventListener('error', done, { once: true });
              })
        )
      ).then(reveal);
      setTimeout(reveal, 3000);
    }

    /* --- Split hero : auto-slide of the mobile slider ---
       Below 750px the two panes are a scroll-snap track ; this only advances it
       on a timer when the section asks for it (data-autoplay). Desktop shows the
       two images side by side, so the timer is stopped above the breakpoint, a
       swipe pauses it for a few seconds, and reduced motion never starts it. */
    _initAutoSlide() {
      this.track = this.querySelector('[data-hero-track]');
      if (!this.track || this.dataset.autoplay !== 'true') return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.slideInterval = parseInt(this.dataset.autoplayInterval || '5000', 10);
      this._onTrackTouch = this._onTrackTouch.bind(this);
      this._onBreakpoint = this._onBreakpoint.bind(this);
      this.track.addEventListener('touchstart', this._onTrackTouch, { passive: true });
      this.track.addEventListener('pointerdown', this._onTrackTouch);
      this._mobileMq = window.matchMedia('(max-width: 749px)');
      if (this._mobileMq.addEventListener) this._mobileMq.addEventListener('change', this._onBreakpoint);
      else this._mobileMq.addListener(this._onBreakpoint); // Safari < 14
      this._onBreakpoint();
    }
    _teardownAutoSlide() {
      this._stopAutoSlide();
      clearTimeout(this._slideResume);
      if (this.track && this._onTrackTouch) {
        this.track.removeEventListener('touchstart', this._onTrackTouch);
        this.track.removeEventListener('pointerdown', this._onTrackTouch);
      }
      if (this._mobileMq) {
        if (this._mobileMq.removeEventListener) this._mobileMq.removeEventListener('change', this._onBreakpoint);
        else this._mobileMq.removeListener(this._onBreakpoint);
      }
    }
    _onBreakpoint() {
      if (this._mobileMq.matches) this._startAutoSlide();
      else this._stopAutoSlide();
    }
    _startAutoSlide() {
      this._stopAutoSlide();
      this._slideTimer = setInterval(() => this._slideNext(), this.slideInterval);
    }
    _stopAutoSlide() {
      if (this._slideTimer) { clearInterval(this._slideTimer); this._slideTimer = null; }
    }
    _slideNext() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      if (max <= 1) return; // nothing to scroll : slider off at this width
      const next = this.track.scrollLeft >= max - 2 ? 0 : this.track.scrollLeft + this.track.clientWidth;
      this.track.scrollTo({ left: next, behavior: 'smooth' });
    }
    _onTrackTouch() {
      // Pause after a swipe, then hand the slider back to the timer
      this._stopAutoSlide();
      clearTimeout(this._slideResume);
      this._slideResume = setTimeout(() => {
        if (this._mobileMq.matches) this._startAutoSlide();
      }, 6000);
    }

    _show() {
      this.toggleBtn.setAttribute('aria-pressed', 'true');
      this.toggleBtn.setAttribute('aria-label', this.labelHide);
      this._tick();
      clearInterval(this._interval);
      this._interval = setInterval(this._tick, 30000); // update every 30s
    }
    _hide() {
      this.toggleBtn.setAttribute('aria-pressed', 'false');
      this.toggleBtn.setAttribute('aria-label', this.labelShow);
      clearInterval(this._interval);
    }
    _onClick() {
      const isOpen = this.toggleBtn.getAttribute('aria-pressed') === 'true';
      if (isOpen) this._hide();
      else this._show();
    }

    _tick() {
      this.label.textContent = parisTimeLabel(this.tz);
      this.label.removeAttribute('hidden');
    }
  }
  customElements.define('pm-home-hero', PmHomeHero);

  /* ---------------------------------------------------------------------------
   * Home wordmark intro - the header logo IS the hero wordmark.
   *   At scroll 0 .pm-header__logo is translated + scaled onto the hidden
   *   .home-hero__wordmark box (which carries the schema width and the 75% cap),
   *   and leaving the top unwinds it back into the header bar. Either way it
   *   re-opens when the visitor comes back to the very top.
   *   Three behaviours, from data-intro-mode on the hero :
   *     - "snap" (default) : --pm-logo-p is binary, 0 at the top and 1 as soon
   *       as the page has moved. The flight is a short CSS transition, so the
   *       logo docks in one go instead of tracking the wheel.
   *     - "scroll" : --pm-logo-p is the eased scroll progress over
   *       data-intro-scroll vh. The measurement target is read live : it rides
   *       up with the hero while the logo shrinks, which makes the docking read
   *       as one movement.
   *     - "hold" (Acne Studios idiom) : no flight at all. A page-level clone of
   *       the wordmark (under the bar in the z stack) stays pinned where the
   *       hero drew it while the hero AND the section after it scroll
   *       underneath. When that section's bottom edge reaches it, the clone
   *       rides up with it, 1px per scrolled px and full size, and slides behind
   *       the bar. The bar logo never moves : CSS-hidden until the clone is
   *       entirely behind the bar (data-intro-docked on the hero), then it is
   *       simply there - no shrink, no transition.
   *       Both the hold and the ride are CSS : the clone is a position:sticky
   *       child of a wrapper laid out in document coordinates, so the browser
   *       keeps it in place itself and nothing here follows the scroll. Moving
   *       it from a scroll handler used to make it jump on phones, where the
   *       page scrolls on the compositor while the handler lags behind.
   *   peterandmay.css holds the same transform as a first-paint estimate ; the
   *   vars are written on the element so the inline value wins over the rule.
   * --------------------------------------------------------------------------*/
  const LOGO_INTRO_VARS = ['--pm-logo-tx', '--pm-logo-ty', '--pm-logo-scale', '--pm-logo-p', '--pm-logo-ink'];
  // Snap mode : anything past this many pixels counts as "the page has moved",
  // so a rubber-band overscroll or a 1px browser restore does not flip the logo.
  const LOGO_SNAP_THRESHOLD = 2;
  let logoIntroTeardown = null;

  // Resolves a CSS length string ("0.5cm", "18px", ...) to px through a probe.
  // display:block is mandatory : base.css hides every empty div (div:empty),
  // which would make the probe measure 0 whatever the length.
  function cssLengthToPx(value) {
    const len = (value || '').trim();
    if (!len) return 0;
    const probe = document.createElement('div');
    probe.style.cssText = 'display:block;position:absolute;visibility:hidden;pointer-events:none;height:0;width:' + len;
    document.body.appendChild(probe);
    const px = probe.getBoundingClientRect().width;
    probe.remove();
    return px;
  }

  function initLogoIntro() {
    if (logoIntroTeardown) { logoIntroTeardown(); logoIntroTeardown = null; }

    const hero = document.querySelector('pm-home-hero[data-logo-intro]');
    const logo = document.querySelector('.pm-header__logo');
    const target = hero && hero.querySelector('[data-wordmark-target]');
    // The <a> wrapper is the untransformed reference : a child transform never
    // moves the parent's border box, so its rect stays the docked slot.
    const slot = logo && logo.parentElement;
    if (!hero || !logo || !target || !slot) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);
    const mode = hero.dataset.introMode;
    const hold = mode === 'hold';
    const snap = !hold && mode !== 'scroll';
    // Hold mode pins the logo through the section right after the hero (theme
    // sections are siblings, each under its .shopify-section wrapper). Nothing
    // after the hero : the hero is the hold itself, the logo leaves with its edge.
    // That section may name the box whose bottom edge does the releasing
    // (data-logo-hold-edge on its media grid, see home-carousel-2col.liquid),
    // so the logo tracks the images and never hangs over the padding under them.
    const heroBox = hero.closest('.shopify-section') || hero;
    const holdSection = heroBox.nextElementSibling || heroBox;
    const holdEl = hold ? (holdSection.querySelector('[data-logo-hold-edge]') || holdSection) : null;
    // Hold mode paints a clone of the wordmark at page level, inside a wrapper
    // laid out in document coordinates (under the bar in the z stack, so it can
    // slide behind it). The hero's own box stays in place, unpainted, as the
    // measurement target.
    let pinned = null;
    let holdBox = null;
    let dockObserver = null;
    let dockBarBottom = null;
    let holdResize = null;
    // Gap kept between the wordmark's bottom edge and the hold box's bottom
    // edge, so the logo stops short of the edge instead of touching it. The
    // value is the --pm-wordmark-hold-gap token (peterandmay.css) ; custom
    // properties come back unresolved, so a probe turns the length into px.
    let holdGap = 0;
    if (hold) {
      pinned = target.cloneNode(true);
      pinned.classList.add('home-hero__wordmark--pinned');
      pinned.removeAttribute('data-wordmark-target');
      holdBox = document.createElement('div');
      holdBox.className = 'home-hero__wordmark-hold';
      holdBox.appendChild(pinned);
      document.body.appendChild(holdBox);
      target.style.visibility = 'hidden';
      holdGap = cssLengthToPx(getComputedStyle(pinned).getPropertyValue('--pm-wordmark-hold-gap'));
    }
    const bar = slot.closest('.pm-header__bar') || slot;
    let raf = null;
    let last = null;

    /* Hold mode : poses the wrapper, and CSS does the rest. It runs from the
       point where the wordmark holds down to the hold box's bottom edge minus
       the gap ; the sticky child clings to that top offset, then rides up with
       the wrapper's bottom edge, 1px per scrolled px. Called on load, on resize
       and when either box changes size - never on scroll. */
    function layoutHold() {
      const t = target.getBoundingClientRect();
      if (!t.width) return;
      const y = window.scrollY;
      // The wrapper is absolute : its origin depends on its containing block,
      // measured rather than assumed to be the document origin.
      holdBox.style.top = '0px';
      holdBox.style.left = '0px';
      const origin = holdBox.getBoundingClientRect();
      const topDoc = t.top + y; // document coords : the box the hero drew at scroll 0
      const releaseDoc = holdEl.getBoundingClientRect().bottom + y - holdGap;
      holdBox.style.left = (t.left - origin.left).toFixed(2) + 'px';
      holdBox.style.top = (topDoc - (origin.top + y)).toFixed(2) + 'px';
      holdBox.style.width = t.width.toFixed(2) + 'px';
      holdBox.style.height = Math.max(0, releaseDoc - topDoc).toFixed(2) + 'px';
      // Sticky offset : the viewport row the hero drew the wordmark on, which at
      // scroll 0 is its document position - the hero sits at the top of the page.
      pinned.style.top = topDoc.toFixed(2) + 'px';
      // Bar logo : there the moment the clone is entirely behind the bar, gone
      // again as soon as it slides back out - no transition either way. Once the
      // clone has been released it rides with the wrapper's bottom edge, so that
      // edge crossing the bar is the same moment. An observer rather than scroll
      // math : the browser wakes us on the right pixel, nothing tracks the wheel.
      // Only the bar height feeds it, so it survives every relayout but a
      // breakpoint change - the hold box growing as its videos load must not
      // rebuild it, or the flip would be re-armed for nothing.
      const barBottom = bar.getBoundingClientRect().bottom;
      if (dockObserver && barBottom === dockBarBottom) return;
      dockBarBottom = barBottom;
      if (dockObserver) dockObserver.disconnect();
      dockObserver = new IntersectionObserver(
        (entries) => {
          const r = entries[entries.length - 1].boundingClientRect;
          hero.dataset.introDocked = r.bottom <= barBottom ? 'true' : 'false';
        },
        { rootMargin: -barBottom.toFixed(2) + 'px 0px 0px 0px', threshold: 0 }
      );
      dockObserver.observe(holdBox);
    }

    function apply() {
      raf = null;
      let eased;
      if (snap) {
        eased = window.scrollY > LOGO_SNAP_THRESHOLD ? 1 : 0;
        // Docked and staying docked : the transform is the identity whatever the
        // boxes measure, so there is nothing to recompute on every scroll frame.
        if (eased === 1 && last === 1) return;
      } else {
        const travel = Math.max(1, ((parseFloat(hero.dataset.introScroll) || 60) / 100) * window.innerHeight);
        const p = clamp01(window.scrollY / travel);
        eased = p * p * (3 - 2 * p); // smoothstep - leaves and lands softly
      }
      const from = target.getBoundingClientRect();
      const to = slot.getBoundingClientRect();
      if (!from.width || !to.width) return;
      logo.style.setProperty('--pm-logo-tx', (from.left + from.width / 2 - (to.left + to.width / 2)).toFixed(2) + 'px');
      logo.style.setProperty('--pm-logo-ty', (from.top + from.height / 2 - (to.top + to.height / 2)).toFixed(2) + 'px');
      logo.style.setProperty('--pm-logo-scale', (from.width / to.width).toFixed(4));
      logo.style.setProperty('--pm-logo-p', eased.toFixed(4));
      // Ink flips late : white while it flies over the image, black by the time
      // it reaches the opaque bar, where white would disappear.
      logo.style.setProperty('--pm-logo-ink', clamp01((eased - 0.55) / 0.3).toFixed(3));
      last = eased;
    }
    function schedule() { if (!raf) raf = requestAnimationFrame(apply); }

    // Fonts and the hero image land after init and can shift the boxes, hence
    // the load listener on both paths. Hold mode never listens to scroll : its
    // whole movement is the sticky child, and a handler racing the compositor is
    // what made the logo jump on phones.
    const relayout = hold ? layoutHold : schedule;
    relayout();
    if (!hold) window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', relayout);
    window.addEventListener('load', relayout);
    // The hold box is a grid of images and videos : they land after init and
    // change its height, which moves the release edge.
    if (hold && 'ResizeObserver' in window) {
      holdResize = new ResizeObserver(() => layoutHold());
      holdResize.observe(holdEl);
      holdResize.observe(hero);
    }

    logoIntroTeardown = () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', relayout);
      window.removeEventListener('load', relayout);
      if (dockObserver) dockObserver.disconnect();
      if (holdResize) holdResize.disconnect();
      LOGO_INTRO_VARS.forEach((v) => logo.style.removeProperty(v));
      if (holdBox) holdBox.remove();
      target.style.removeProperty('visibility');
      delete hero.dataset.introDocked;
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initLogoIntro);
  } else {
    initLogoIntro();
  }
  // Theme editor : re-render of the header or the hero swaps the measured nodes
  document.addEventListener('shopify:section:load', initLogoIntro);
  document.addEventListener('shopify:section:unload', initLogoIntro);
  // Hold mode measures the section placed after the hero : a reorder moves it
  document.addEventListener('shopify:section:reorder', initLogoIntro);

  /* ---------------------------------------------------------------------------
   * Carousels - <pm-carousel-2col>, <pm-carousel-image>, <pm-carousel-products>
   *   Generic scroll-snap carousel with prev/next button support.
   *   Native CSS handles the scroll mechanic; JS only provides keyboard nav
   *   and arrow button handling.
   * --------------------------------------------------------------------------*/
  class PmCarousel extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-viewport]');
      this.track = this.querySelector('[data-track]');
      // Arrows normally live inside the carousel. data-slide-scope - the attribute that
      // already widens the [data-slide-sync] lookup - widens this one too, so a section can
      // put its chevrons outside the media column, on the flat page rather than over the
      // photography. Only the DECLARED scope is searched, never the parent by default, or
      // two carousels sitting side by side would claim each other's buttons.
      this.navScope = (this.dataset.slideScope && this.closest(this.dataset.slideScope)) || null;
      this.prevBtn = this.querySelector('[data-prev]') || (this.navScope && this.navScope.querySelector('[data-prev]'));
      this.nextBtn = this.querySelector('[data-next]') || (this.navScope && this.navScope.querySelector('[data-next]'));
      // data-hide-edge-nav : the arrow that has nowhere left to go is disabled, for the CSS
      // to fade out (see .home-marie__chevron:disabled). Opt-in, not the default - the PDP
      // hero keeps both arrows at the edges, and autoplay still wraps past the last slide.
      this.edgeNav = this.hasAttribute('data-hide-edge-nav');
      if (!this.track) return;
      this._onPrev = this._onPrev.bind(this);
      this._onNext = this._onNext.bind(this);
      this._onUserInteract = this._onUserInteract.bind(this);
      this._onMouseEnter = this._onMouseEnter.bind(this);
      this._onMouseLeave = this._onMouseLeave.bind(this);
      this._onScroll = this._onScroll.bind(this);

      if (this.prevBtn) this.prevBtn.addEventListener('click', this._onPrev);
      if (this.nextBtn) this.nextBtn.addEventListener('click', this._onNext);

      // Autoplay (opt-in via data-autoplay='true' on the host element)
      const autoplay = this.dataset.autoplay === 'true' || this.dataset.autoplay === '1';
      if (autoplay) {
        this.interval = parseInt(this.dataset.autoplayInterval || '4000', 10);
        this.track.addEventListener('pointerdown', this._onUserInteract);
        this.track.addEventListener('touchstart', this._onUserInteract, { passive: true });
        this.addEventListener('mouseenter', this._onMouseEnter);
        this.addEventListener('mouseleave', this._onMouseLeave);
        this._startAutoplay();
      }

      // Scroll-driven UI, both opt-in :
      //   - progress bar   : a [data-progress-thumb] element inside the carousel
      //   - slide-linked groups : every [data-slide-sync] group whose children map 1:1
      //                     to the slides, only the active one carrying .is-active.
      //                     Looked up inside the carousel's parent by default ;
      //                     data-slide-scope='<selector>' widens that to an ancestor so a
      //                     group living outside the media column (the chapter text in
      //                     Meet Marie) follows the slides too. One carousel per scope -
      //                     two would fight over the same groups.
      this.progressThumb = this.querySelector('[data-progress-thumb]');
      const scope = this.navScope || this.parentElement;
      this.slideGroups = scope
        ? Array.from(scope.querySelectorAll('[data-slide-sync]')).map((g) => Array.from(g.children))
        : [];

      // Direct-jump nav - any [data-goto="<index>"] inside the same scope as the
      // sync groups scrolls straight to that slide. That is how a thumbnail strip
      // works : the strip is ALSO a [data-slide-sync] group, so it lights its own
      // active item on scroll and only owes us the jump on click.
      this.gotoBtns = scope ? Array.from(scope.querySelectorAll('[data-goto]')) : [];
      if (this.gotoBtns.length) {
        this._onGoto = this._onGoto.bind(this);
        this.gotoBtns.forEach((btn) => btn.addEventListener('click', this._onGoto));
      }

      if (this.progressThumb || this.slideGroups.length || this.edgeNav) {
        this.track.addEventListener('scroll', this._onScroll, { passive: true });
        window.addEventListener('resize', this._onScroll);
        this._sync();
      }

      // Theme editor : selecting a media block scrolls its slide into view, otherwise the
      // merchant edits block 3 while slide 1 is still on screen.
      if (window.Shopify && window.Shopify.designMode) {
        this._onBlockSelect = this._onBlockSelect.bind(this);
        document.addEventListener('shopify:block:select', this._onBlockSelect);
      }

      // Mouse drag-to-scroll (desktop) - native overflow handles touch/trackpad
      this._onDragStart = this._onDragStart.bind(this);
      this._onDragMove = this._onDragMove.bind(this);
      this._onDragEnd = this._onDragEnd.bind(this);
      this._suppressClick = this._suppressClick.bind(this);
      this._noNativeDrag = (e) => e.preventDefault();
      this.track.addEventListener('pointerdown', this._onDragStart);
      this.track.addEventListener('dragstart', this._noNativeDrag);
      this.track.style.cursor = 'grab';
    }
    disconnectedCallback() {
      if (this.prevBtn) this.prevBtn.removeEventListener('click', this._onPrev);
      if (this.nextBtn) this.nextBtn.removeEventListener('click', this._onNext);
      this.track.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onScroll);
      this.track.removeEventListener('pointerdown', this._onDragStart);
      this.track.removeEventListener('dragstart', this._noNativeDrag);
      if (this._onBlockSelect) document.removeEventListener('shopify:block:select', this._onBlockSelect);
      if (this._onGoto) this.gotoBtns.forEach((btn) => btn.removeEventListener('click', this._onGoto));
      this._stopAutoplay();
    }
    _onGoto(e) {
      const i = parseInt(e.currentTarget.dataset.goto, 10);
      if (!Number.isNaN(i)) this._scrollToIndex(i);
    }
    _onDragStart(e) {
      if (e.pointerType === 'touch' || e.button !== 0) return; // native scroll for touch
      this._dragPending = true;
      this._dragging = false;
      this._dragStartX = e.clientX;
      this._dragStartScroll = this.track.scrollLeft;
      this._dragStartIndex = this._currentIndex();
      this.track.addEventListener('pointermove', this._onDragMove);
      this.track.addEventListener('pointerup', this._onDragEnd);
      this.track.addEventListener('pointercancel', this._onDragEnd);
    }
    _onDragMove(e) {
      if (!this._dragPending) return;
      const dx = e.clientX - this._dragStartX;
      if (!this._dragging) {
        if (Math.abs(dx) < 4) return; // wait for a real drag before hijacking clicks
        this._dragging = true;
        try { this.track.setPointerCapture(e.pointerId); } catch (_) {}
        this.track.style.scrollSnapType = 'none';
        // The tracks carry `scroll-behavior: smooth` in CSS, and the scrollLeft setter
        // obeys it : every drag frame would start its own animation that the next frame
        // overrides, so the track lags behind the cursor and never catches up. Instant
        // for the duration of the drag only - the scrollTo/scrollBy calls elsewhere pass
        // behavior:'smooth' explicitly, so they keep their easing either way.
        this.track.style.scrollBehavior = 'auto';
        this.track.style.cursor = 'grabbing';
      }
      this.track.scrollLeft = this._dragStartScroll - dx; // follow cursor during drag
    }
    _onDragEnd(e) {
      this._dragPending = false;
      this.track.removeEventListener('pointermove', this._onDragMove);
      this.track.removeEventListener('pointerup', this._onDragEnd);
      this.track.removeEventListener('pointercancel', this._onDragEnd);
      if (this._dragging) {
        this._dragging = false;
        this.track.style.cursor = 'grab';
        this.track.style.scrollBehavior = ''; // back to the CSS smooth for the snap below
        try { this.track.releasePointerCapture(e.pointerId); } catch (_) {}
        // Snap to a WHOLE slide in the drag direction - no half-slide stops.
        const dx = e.clientX - this._dragStartX;
        const slideW = this._slideWidth() || 1;
        let steps = Math.round(-dx / slideW);
        if (steps === 0 && Math.abs(dx) >= 40) steps = dx < 0 ? 1 : -1; // any deliberate drag moves ≥1
        this._scrollToIndex(this._dragStartIndex + steps);
        // Restore CSS scroll-snap once the smooth scroll has settled
        clearTimeout(this._snapRestore);
        this._snapRestore = setTimeout(() => { this.track.style.scrollSnapType = ''; }, 400);
        // Swallow the click that follows a drag so slide links don't navigate.
        this.track.addEventListener('click', this._suppressClick, { capture: true, once: true });
        setTimeout(() => this.track.removeEventListener('click', this._suppressClick, { capture: true }), 350);
      }
    }
    _suppressClick(e) { e.preventDefault(); e.stopPropagation(); }
    _slideOffset(i) {
      const slides = this.track.children;
      if (!slides.length || !slides[i]) return 0;
      return slides[i].offsetLeft - slides[0].offsetLeft;
    }
    _currentIndex() {
      const slides = this.track.children;
      const sl = this.track.scrollLeft;
      let best = 0, bestDist = Infinity;
      for (let i = 0; i < slides.length; i++) {
        const d = Math.abs(this._slideOffset(i) - sl);
        if (d < bestDist) { bestDist = d; best = i; }
      }
      return best;
    }
    _scrollToIndex(i) {
      const slides = this.track.children;
      if (!slides.length) return;
      const clamped = Math.max(0, Math.min(i, slides.length - 1));
      this.track.scrollTo({ left: this._slideOffset(clamped), behavior: 'smooth' });
    }
    _onScroll() {
      if (this._syncRaf) return;
      this._syncRaf = requestAnimationFrame(() => {
        this._syncRaf = null;
        this._sync();
      });
    }
    _sync() {
      this._updateProgress();
      this._updateSlideGroups();
      this._updateEdgeNav();
    }
    // A track shorter than its viewport has no travel at all : both ends are the same
    // point, so both arrows go. The 2px slack absorbs sub-pixel scroll positions, which
    // never land exactly on 0 or on the maximum after a smooth scroll.
    _updateEdgeNav() {
      if (!this.edgeNav) return;
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      const max = scrollWidth - clientWidth;
      if (this.prevBtn) this.prevBtn.disabled = max <= 2 || scrollLeft <= 2;
      if (this.nextBtn) this.nextBtn.disabled = max <= 2 || scrollLeft >= max - 2;
    }
    _updateSlideGroups() {
      if (!this.slideGroups.length) return;
      const active = this._currentIndex();
      this.slideGroups.forEach((slots) => {
        slots.forEach((el, i) => el.classList.toggle('is-active', i === active));
      });
    }
    _onBlockSelect(e) {
      const id = e.detail && e.detail.blockId;
      if (!id) return;
      // Two instances of the same section share their block ids : only the one holding the
      // selected block reacts, otherwise both carousels jump at once.
      const root = this.closest('.shopify-section');
      if (root && e.target && !root.contains(e.target)) return;
      const i = Array.from(this.track.children).findIndex((s) => s.dataset.blockId === id);
      if (i >= 0) this._scrollToIndex(i);
    }
    _updateProgress() {
      if (!this.progressThumb) return;
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      if (scrollWidth <= clientWidth) {
        this.progressThumb.style.width = '100%';
        this.progressThumb.style.left = '0%';
        return;
      }
      const widthPct = (clientWidth / scrollWidth) * 100;
      const max = scrollWidth - clientWidth;
      const posPct = (scrollLeft / max) * (100 - widthPct);
      this.progressThumb.style.width = widthPct + '%';
      this.progressThumb.style.left = posPct + '%';
    }
    _slideWidth() {
      const first = this.track.firstElementChild;
      if (!first) return 0;
      return first.offsetWidth + (parseInt(getComputedStyle(this.track).columnGap || '0', 10) || 0);
    }
    _onPrev() { this.track.scrollBy({ left: -this._slideWidth(), behavior: 'smooth' }); }
    _onNext() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      if (this.track.scrollLeft >= max - 2) {
        this.track.scrollTo({ left: 0, behavior: 'smooth' }); // loop back to start
      } else {
        this.track.scrollBy({ left: this._slideWidth(), behavior: 'smooth' });
      }
    }
    _startAutoplay() {
      this._stopAutoplay();
      this._autoplayTimer = setInterval(() => this._onNext(), this.interval);
    }
    _stopAutoplay() {
      if (this._autoplayTimer) { clearInterval(this._autoplayTimer); this._autoplayTimer = null; }
    }
    _onUserInteract() {
      // Pause briefly after user touch/drag
      this._stopAutoplay();
      clearTimeout(this._resumeTimer);
      this._resumeTimer = setTimeout(() => this._startAutoplay(), 6000);
    }
    _onMouseEnter() { this._stopAutoplay(); }
    _onMouseLeave() { this._startAutoplay(); }
  }
  customElements.define('pm-carousel-2col', class extends PmCarousel {});
  customElements.define('pm-carousel-image', class extends PmCarousel {});
  customElements.define('pm-carousel-products', class extends PmCarousel {});

  /* ---------------------------------------------------------------------------
   * <pm-card-media> - per product-card image slider
   *   Black chevrons step through the product images ; pagination dots reflect
   *   and control the current image. Native scroll-snap handles the mechanic.
   * --------------------------------------------------------------------------*/
  class PmCardMedia extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-media-track]');
      if (!this.track) return;
      this.prevBtn = this.querySelector('[data-media-prev]');
      this.nextBtn = this.querySelector('[data-media-next]');
      this.dots = Array.from(this.querySelectorAll('[data-media-dot]'));
      /* Present only when the card has more colorways than the row draws. */
      this.moreDot = this.querySelector('[data-media-dot-more]');
      this._onPrev = (e) => { e.preventDefault(); this._step(-1); };
      this._onNext = (e) => { e.preventDefault(); this._step(1); };
      this._onScroll = () => this._syncDots();
      if (this.prevBtn) this.prevBtn.addEventListener('click', this._onPrev);
      if (this.nextBtn) this.nextBtn.addEventListener('click', this._onNext);
      this.dots.forEach((dot, i) => {
        dot.addEventListener('click', (e) => { e.preventDefault(); this._goTo(i); });
        /* Colorway swatches : hovering one flips the card to that colorway right
           away, no click needed. Mouse only - on touch, pointerenter fires with
           the tap itself and would double up with the click. */
        if (dot.classList.contains('pm-card__dot--swatch')) {
          dot.addEventListener('pointerenter', (e) => {
            if (e.pointerType === 'mouse') this._goTo(i);
          });
        }
      });
      this.track.addEventListener('scroll', this._onScroll, { passive: true });
      this._syncDots();
    }
    _count() { return this.track.children.length; }
    /* Bounded : during the iOS rubber-band scrollLeft runs past the last snap
       point (or below 0), which used to round to an index no dot owns. */
    _index() {
      const w = this.track.clientWidth || 1;
      const raw = Math.round(this.track.scrollLeft / w);
      return Math.max(0, Math.min(raw, this._count() - 1));
    }
    /* Targets the slide's real offset, not index x clientWidth : on a 2-col
       phone grid the card is often a half-pixel wide (185.5px at 375px), so
       clientWidth rounds up and the computed target overshoots the scroller
       (186 vs a 185 max) - Safari then hesitates on the snap. Clamped to the
       scrollable max for the same reason. */
    _goTo(i) {
      const clamped = Math.max(0, Math.min(i, this._count() - 1));
      const slide = this.track.children[clamped];
      if (!slide) return;
      const offset = slide.getBoundingClientRect().left - this.track.getBoundingClientRect().left + this.track.scrollLeft;
      const max = this.track.scrollWidth - this.track.clientWidth;
      this.track.scrollTo({ left: Math.max(0, Math.min(offset, max)), behavior: 'smooth' });
    }
    _step(dir) { this._goTo(this._index() + dir); }
    _syncDots() {
      if (this._raf) return;
      this._raf = requestAnimationFrame(() => {
        this._raf = null;
        const idx = this._index();
        /* Past the cap no disc owns the slide, so the active state folds onto the
           counter rather than vanishing from the row. */
        const beyondCap = !!this.moreDot && idx >= this.dots.length;
        this.dots.forEach((d, i) => {
          if (!beyondCap && i === idx) d.setAttribute('aria-current', 'true');
          else d.removeAttribute('aria-current');
        });
        if (this.moreDot) {
          if (beyondCap) this.moreDot.setAttribute('aria-current', 'true');
          else this.moreDot.removeAttribute('aria-current');
        }
        this._syncMeta(idx);
      });
    }
    /* Declinations mode : the meta row (name / colour / price) follows the
       visible slide - each slide is a distinct colorway product carrying
       data-slide-* attributes (see snippets/pm-product-card.liquid). */
    _syncMeta(idx) {
      const slide = this.track.children[idx];
      if (!slide || !slide.dataset || !slide.dataset.slideTitle) return;
      const name = this.querySelector('[data-card-name]');
      const variant = this.querySelector('[data-card-variant]');
      const price = this.querySelector('[data-card-price]');
      const soon = this.querySelector('[data-card-soon]');
      const isNew = this.querySelector('[data-card-new]');
      if (name) {
        // slideName is the model alone ("riptide"), slideTitle the raw fallback
        name.textContent = slide.dataset.slideName || slide.dataset.slideTitle;
        if (slide.dataset.slideUrl) name.href = slide.dataset.slideUrl;
      }
      if (variant) variant.textContent = slide.dataset.slideColor || '';
      /* Optical frames ship an empty data-slide-price (no price anywhere on the
         site) : blank the cell rather than leaving the previous slide's price
         behind, a family can mix an optical frame and its SUN version.

         Two cells, not one : every price is printed in both currencies and CSS
         shows the one the visitor picked in the footer (see
         snippets/pm-money.liquid), so writing textContent on the wrapper would
         wipe the pair out. A colorway with no dollar RRP of its own falls back
         to its euro price, exactly as the Liquid cell does. */
      if (price) {
        const eur = slide.dataset.slidePrice || '';
        const usd = slide.dataset.slidePriceUsd || eur;
        const eurCell = price.querySelector('[data-money-eur]');
        const usdCell = price.querySelector('[data-money-usd]');
        if (eurCell) eurCell.textContent = eur;
        if (usdCell) usdCell.textContent = usd;
        if (!eurCell && !usdCell) price.textContent = eur;
      }
      /* Coming soon is per colorway too : a family can have one coloris out and
         the next one still to come, so the line follows the slide rather than
         staying on for the whole card. */
      if (soon) soon.hidden = slide.dataset.slideSoon !== '1';
      /* New is per colorway as well : a family can have a brand new coloris
         sitting next to one that has been out for a season. */
      if (isNew) isNew.hidden = slide.dataset.slideNew !== '1';
    }
  }
  customElements.define('pm-card-media', PmCardMedia);

  /* ---------------------------------------------------------------------------
   * <pm-cart-drawer> - right-side cart drawer
   *   Opens when user clicks the header Cart link.
   *   Updates line items via /cart/change.js + re-renders via Section Rendering.
   *   Auto-closes when cart becomes empty.
   * --------------------------------------------------------------------------*/
  class PmCartDrawer extends HTMLElement {
    connectedCallback() {
      this._bind();
      this._wireCartLinks();
    }
    disconnectedCallback() { this._unbind(); }

    _bind() {
      this._onCloseClick = this._onCloseClick.bind(this);
      this._onBackdropClick = this._onBackdropClick.bind(this);
      this._onKeyDown = this._onKeyDown.bind(this);
      this._onItemAction = this._onItemAction.bind(this);
      this._onNoteInput = this._onNoteInput.bind(this);
      this._onNoteBlur = this._onNoteBlur.bind(this);
      this._onNoteToggle = this._onNoteToggle.bind(this);

      const backdrop = document.querySelector('[data-cart-backdrop]');
      if (backdrop) backdrop.addEventListener('click', this._onBackdropClick);
      document.addEventListener('keydown', this._onKeyDown);

      /* Delegated : add-to-cart and every qty change re-render the drawer through
         `innerHTML = ...`, so a listener bound on the close button itself dies on
         the first refresh. The listener lives on the element, which survives. */
      this.addEventListener('click', this._onCloseClick);
      this.addEventListener('click', this._onItemAction);
      this.addEventListener('change', this._onItemAction);
      /* Cart note : `input` bubbles, `blur` does not - `focusout` is its
         bubbling twin, so both survive the innerHTML swaps too. */
      this.addEventListener('input', this._onNoteInput);
      this.addEventListener('focusout', this._onNoteBlur);
      this.addEventListener('change', this._onNoteToggle);
    }
    _unbind() {
      document.removeEventListener('keydown', this._onKeyDown);
      clearTimeout(this._noteTimer);
    }

    _wireCartLinks() {
      // Intercept any link to /cart : open drawer instead of navigate.
      document.addEventListener('click', (e) => {
        const a = e.target.closest('a[href$="/cart"], a[href*="/cart?"]');
        if (!a) return;
        // Hold ctrl/meta to allow normal navigation
        if (e.ctrlKey || e.metaKey || e.shiftKey) return;
        e.preventDefault();
        this.open();
      });
    }

    open() {
      // A PDP info panel shares the drawer layer - never stack the two
      document.querySelectorAll('pm-pdp-modal[data-open="true"]').forEach((panel) => panel.close());
      document.body.dataset.cartOpen = 'true';
      this.removeAttribute('hidden');
      const focusable = this.querySelector('button, a, input');
      if (focusable) focusable.focus({ preventScroll: true });
    }
    close() {
      delete document.body.dataset.cartOpen;
    }

    _onCloseClick(e) {
      if (!e.target.closest('[data-cart-close]')) return;
      e.preventDefault();
      this.close();
    }
    _onBackdropClick() { this.close(); }
    _onKeyDown(e) {
      if (e.key === 'Escape' && document.body.dataset.cartOpen === 'true') this.close();
    }

    async _onItemAction(e) {
      const remove = e.target.closest('[data-cart-remove]');
      const inc = e.target.closest('[data-qty-increment]');
      const dec = e.target.closest('[data-qty-decrement]');
      const qtyInput = e.target.matches('[data-qty-input]') ? e.target : null;
      const line = (remove || inc || dec || qtyInput)?.closest('[data-cart-line]');
      if (!line) return;
      const lineKey = line.dataset.cartLine;
      let newQty;
      if (remove) newQty = 0;
      else {
        const input = line.querySelector('[data-qty-input]');
        if (!input) return;
        if (inc) newQty = (parseInt(input.value, 10) || 0) + 1;
        else if (dec) newQty = Math.max(0, (parseInt(input.value, 10) || 0) - 1);
        else newQty = parseInt(input.value, 10);
      }
      if (Number.isNaN(newQty)) return;
      e.preventDefault();
      await this._updateLine(lineKey, newQty);
    }

    /* ---- Cart note (settings.show_cart_note) -------------------------------
       Typing saves through /cart/update.js after a short pause, and the
       textarea also carries form="PmCartDrawerForm" so a Check Out clicked
       before that pause elapses still posts the note with the form. */
    _onNoteInput(e) {
      const field = e.target.closest('[data-cart-note]');
      if (!field) return;
      this._pendingNote = field.value;
      clearTimeout(this._noteTimer);
      this._noteTimer = setTimeout(() => this._saveNote(), 500);
    }

    _onNoteBlur(e) {
      if (!e.target.closest('[data-cart-note]')) return;
      this._saveNote();
    }

    /* Unticking hides the field, so it has to drop the note too - a hidden
       textarea would otherwise keep sending its text to the order. */
    _onNoteToggle(e) {
      const box = e.target.closest('[data-cart-note-toggle]');
      if (!box) return;
      const field = this.querySelector('[data-cart-note]');
      if (!field) return;
      if (box.checked) {
        field.focus({ preventScroll: true });
        return;
      }
      if (field.value === '') return;
      field.value = '';
      this._pendingNote = '';
      this._saveNote();
    }

    async _saveNote() {
      clearTimeout(this._noteTimer);
      if (this._pendingNote === undefined) return;
      const note = this._pendingNote;
      this._pendingNote = undefined;
      try {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ note }),
        });
      } catch (err) {
        console.error('[pm-cart-drawer] note save failed', err);
        this._pendingNote = note; // keep it for the next flush
      }
    }

    async _updateLine(key, qty) {
      // The re-render below echoes the server's cart.note - push ours first.
      await this._saveNote();
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity: qty, sections: 'pm-cart-drawer' }),
        });
        const data = await res.json();
        if (data.sections && data.sections['pm-cart-drawer']) {
          this._replaceWithRenderedSection(data.sections['pm-cart-drawer']);
        }
        if ((data.item_count || 0) === 0) {
          // Auto-close on empty (per spec)
          setTimeout(() => this.close(), 600);
        }
        this._dispatchCartUpdate(data);
      } catch (err) {
        console.error('[pm-cart-drawer] update failed', err);
      }
    }

    _replaceWithRenderedSection(html) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const next = doc.querySelector('pm-cart-drawer');
      if (!next) return;
      /* A note typed but not yet saved would be overwritten by the server copy
         that came back with the section - carry the live text, the focus and
         the caret across the swap. */
      const live = this.querySelector('[data-cart-note]');
      const liveValue = live ? live.value : null;
      const liveHadFocus = live ? document.activeElement === live : false;
      const caret = live ? live.selectionStart : 0;
      /* The box is only rendered ticked when the cart already holds a note, so
         an opened but still empty field would fold back shut on its own. */
      const liveBox = this.querySelector('[data-cart-note-toggle]');
      const liveBoxChecked = liveBox ? liveBox.checked : null;
      // Replace inner contents only (preserve element listeners)
      this.innerHTML = next.innerHTML;
      const fresh = this.querySelector('[data-cart-note]');
      if (fresh && liveValue !== null) {
        if (fresh.value !== liveValue) fresh.value = liveValue;
        if (liveHadFocus) {
          fresh.focus({ preventScroll: true });
          fresh.setSelectionRange(caret, caret);
        }
      }
      const freshBox = this.querySelector('[data-cart-note-toggle]');
      if (freshBox && liveBoxChecked !== null) freshBox.checked = liveBoxChecked;
    }

    _dispatchCartUpdate(cart) {
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
    }
  }
  customElements.define('pm-cart-drawer', PmCartDrawer);

  /* ---------------------------------------------------------------------------
   * <pm-pdp-modal> - right-side panel behind the PDP info rows in 'modal' mode
   *   Opened by any [data-pdp-modal-open="<panel id>"] : the desktop row and its
   *   mobile twin share one panel. Closed by the X, the backdrop or Escape ;
   *   focus goes back to the row that opened it.
   * --------------------------------------------------------------------------*/
  class PmPdpModal extends HTMLElement {
    connectedCallback() {
      this._onKeyDown = this._onKeyDown.bind(this);
      this._onClick = this._onClick.bind(this);
      this.addEventListener('click', this._onClick);
      document.addEventListener('keydown', this._onKeyDown);
    }
    disconnectedCallback() {
      document.removeEventListener('keydown', this._onKeyDown);
    }

    open(trigger) {
      document.querySelectorAll('pm-pdp-modal[data-open="true"]').forEach((other) => {
        if (other !== this) other.close();
      });
      this._trigger = trigger || null;
      this.dataset.open = 'true'; // body scroll lock rides on this (CSS :has)
      const focusable = this.querySelector('[data-pdp-modal-close]:not([aria-hidden]), a, button');
      if (focusable) focusable.focus({ preventScroll: true });
    }
    close() {
      if (this.dataset.open !== 'true') return;
      delete this.dataset.open;
      if (this._trigger) {
        this._trigger.focus({ preventScroll: true });
        this._trigger = null;
      }
    }

    _onClick(e) {
      if (!e.target.closest('[data-pdp-modal-close]')) return;
      e.preventDefault();
      this.close();
    }
    _onKeyDown(e) {
      if (e.key === 'Escape' && this.dataset.open === 'true') this.close();
    }
  }
  customElements.define('pm-pdp-modal', PmPdpModal);

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-pdp-modal-open]');
    if (!trigger) return;
    const panel = document.getElementById(trigger.dataset.pdpModalOpen);
    if (!panel || typeof panel.open !== 'function') return;
    e.preventDefault();
    panel.open(trigger);
  });

  /* ---------------------------------------------------------------------------
   * PDP - variant selection + add-to-cart intercept (opens cart drawer)
   * --------------------------------------------------------------------------*/
  function initPdp() {
    const pdp = document.querySelector('[data-pdp]');
    if (!pdp) return;

    const form = pdp.querySelector('form[id="PdpForm"]');
    if (!form) return;
    const variantIdInput = pdp.querySelector('[data-variant-id]');
    const priceEl = pdp.querySelector('[data-price]');
    const addPriceEl = pdp.querySelector('[data-add-price]');
    const addBtn = pdp.querySelector('[data-add-button]');
    const addText = pdp.querySelector('[data-add-text]');
    // Localized button labels (Liquid `| t` → data attributes)
    const txt = {
      add: (addBtn && addBtn.dataset.textAdd) || 'Add to cart',
      soldout: (addBtn && addBtn.dataset.textSoldout) || 'Sold out',
      adding: (addBtn && addBtn.dataset.textAdding) || 'Adding…',
      added: (addBtn && addBtn.dataset.textAdded) || 'Added',
    };

    function gatherOptions() {
      const opts = [];
      pdp.querySelectorAll('.pm-pdp__option').forEach((fs) => {
        const checked = fs.querySelector('input[data-variant-option]:checked');
        if (checked) opts.push(checked.value);
      });
      return opts;
    }
    function findVariantByOptions(opts) {
      const variants = window.__pdpVariants || [];
      return variants.find((v) => {
        return opts.every((val, i) => v.options[i] === val);
      });
    }
    function formatMoney(cents) {
      // Use Shopify's window.Shopify if available, fallback to basic
      if (window.Shopify && window.Shopify.formatMoney) return window.Shopify.formatMoney(cents);
      return '€' + (cents / 100).toFixed(2).replace('.', ',');
    }
    /* Every price cell holds both currencies and CSS shows one (see
       snippets/pm-money.liquid), so the two halves are written separately.
       The euro side is the variant's own price ; the dollar side is its
       custom.prix_rrp_usd, a VARIANT metafield that Liquid cannot put in the
       variant JSON - sections/main-product.liquid ships it as its own id ->
       string table instead. A variant absent from that table keeps the euro
       amount in the dollar cell, the same fallback the Liquid cell applies.
       A cell rendered without the inner spans keeps the old behaviour. */
    function writePrice(el, variant) {
      if (!el) return;
      const eur = formatMoney(variant.price);
      const eurCell = el.querySelector('[data-money-eur]');
      const usdCell = el.querySelector('[data-money-usd]');
      if (!eurCell && !usdCell) {
        el.textContent = eur;
        return;
      }
      if (eurCell) eurCell.textContent = eur;
      if (usdCell) usdCell.textContent = (window.__pdpVariantsUsd || {})[variant.id] || eur;
    }

    function applyVariant(v) {
      if (!v) return;
      variantIdInput.value = v.id;
      writePrice(priceEl, v);
      writePrice(addPriceEl, v);
      if (addBtn) {
        addBtn.disabled = !v.available;
        if (addText) addText.textContent = v.available ? txt.add : txt.soldout;
      }
    }

    pdp.querySelectorAll('input[data-variant-option]').forEach((r) => {
      r.addEventListener('change', () => {
        const v = findVariantByOptions(gatherOptions());
        applyVariant(v);
      });
    });

    // Accordion : one open at a time (per spec - opening one closes the other)
    const accRows = pdp.querySelectorAll('[data-accordion] details');
    accRows.forEach((row) => {
      row.addEventListener('toggle', () => {
        if (!row.open) return;
        accRows.forEach((other) => {
          if (other !== row && other.open) other.open = false;
        });
      });
    });

    // Intercept add-to-cart submit → use cart/add.js + open drawer
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      // Close Friends early access : no add button in the markup means the product
      // is locked for this visitor. Pressing Enter in a field still lands here, so
      // bail out before touching addBtn.
      if (!addBtn) return;
      const formData = new FormData(form);
      formData.set('sections', 'pm-cart-drawer');
      try {
        addBtn.disabled = true;
        if (addText) addText.textContent = txt.adding;
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/javascript' },
          body: formData,
        });
        const data = await res.json();
        if (data.status) {
          // Error from Shopify
          console.error('[pm-pdp] add error', data);
          if (addText) addText.textContent = data.description || 'Error';
        } else {
          // Refresh cart drawer via Section Rendering
          const cartRes = await fetch('/cart.js');
          const cart = await cartRes.json();
          // Fetch drawer section content
          const secRes = await fetch('/?section_id=pm-cart-drawer');
          const html = await secRes.text();
          const drawer = document.querySelector('pm-cart-drawer');
          if (drawer && html) {
            // Same swap as a qty change, so an in-progress cart note survives
            drawer._replaceWithRenderedSection(html);
            drawer.open();
          }
          if (addText) addText.textContent = txt.added;
          setTimeout(() => {
            if (addText) addText.textContent = txt.add;
            addBtn.disabled = false;
          }, 1200);
        }
      } catch (err) {
        console.error('[pm-pdp] submit failed', err);
        addBtn.disabled = false;
        if (addText) addText.textContent = txt.add;
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPdp);
  } else {
    initPdp();
  }

  /* ---------------------------------------------------------------------------
   * PDP - "see the details" shortcut of the info card
   *   The dimensions, the materials and the catalogue lines live far below the
   *   fold, and they are printed TWICE : the band column on desktop, the
   *   accordion group on mobile - whichever of the two the breakpoint hides is
   *   display:none, so the target has to be picked at click time rather than
   *   written into the href. getClientRects() is the test : it comes back empty
   *   for a hidden element, and unlike offsetParent it does not lie about the
   *   sticky spec column.
   *   The mobile row is a <details> that starts closed - opened here, otherwise
   *   the jump lands on a summary line with the details still folded away.
   *   Both landing spots carry their own scroll-margin-top in peterandmay.css,
   *   which is what keeps them clear of the sticky header.
   * --------------------------------------------------------------------------*/
  function initPdpDetailsJump() {
    const link = document.querySelector('[data-pdp-details-jump]');
    // Guarded : shopify:section:load fires for every section of the page, and
    // the link only comes back as a new node when the PDP itself re-renders
    if (!link || link._pmDetailsJump) return;
    link._pmDetailsJump = true;

    const shown = (el) => !!el && el.getClientRects().length > 0;

    link.addEventListener('click', (e) => {
      const row = document.querySelector('[data-pdp-details-row]');
      const column = document.querySelector('[data-pdp-details]');
      let target = null;
      if (shown(row)) {
        row.open = true;
        target = row;
      } else if (shown(column)) {
        target = column;
      }
      // Nothing visible : let the href do whatever the browser makes of it
      if (!target) return;
      e.preventDefault();
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPdpDetailsJump);
  } else {
    initPdpDetailsJump();
  }
  // Theme editor : the section comes back as brand new markup
  document.addEventListener('shopify:section:load', initPdpDetailsJump);

  /* ---------------------------------------------------------------------------
   * <pm-filter-overlay> + <pm-filter-bar> - filter open/close
   * --------------------------------------------------------------------------*/
  class PmFilterOverlay extends HTMLElement {
    connectedCallback() {
      this._onKey = this._onKey.bind(this);
      this._onSubmit = this._onSubmit.bind(this);
      this._trigger = document.querySelector('[data-filter-trigger]');
      if (this._trigger) {
        this._onTrigger = (e) => { e.preventDefault(); this.toggle(); };
        this._trigger.addEventListener('click', this._onTrigger);
      }
      document.addEventListener('keydown', this._onKey);
      const form = this.querySelector('form');
      if (form) form.addEventListener('submit', this._onSubmit);
      this._initPaging();
      this._initLiveCount(form);
      // Mutual exclusion with the Sort dropdown : opening one closes the other
      this._sort = document.querySelector('.pm-collection__sort');
      if (this._sort) {
        this._sort.addEventListener('toggle', () => {
          if (this._sort.open && this.dataset.open === 'true') this.close();
        });
      }
    }
    disconnectedCallback() {
      if (this._trigger) this._trigger.removeEventListener('click', this._onTrigger);
      document.removeEventListener('keydown', this._onKey);
    }

    /* Horizontal paging of the filter columns : Previous/Next + progress thumb */
    _initPaging() {
      const viewport = this.querySelector('[data-filter-viewport]');
      const nav = this.querySelector('[data-filter-nav]');
      const progress = this.querySelector('[data-filter-progress]');
      if (!viewport || !nav || !progress) return;
      const thumb = progress.querySelector('[data-filter-progress-thumb]');
      const prevBtn = this.querySelector('[data-filter-prev]');
      const nextBtn = this.querySelector('[data-filter-next]');
      const sync = () => {
        const max = viewport.scrollWidth - viewport.clientWidth;
        if (max <= 1) { nav.hidden = true; progress.hidden = true; return; }
        nav.hidden = false;
        progress.hidden = false;
        const ratio = viewport.clientWidth / viewport.scrollWidth;
        thumb.style.width = (ratio * 100) + '%';
        thumb.style.left = ((viewport.scrollLeft / viewport.scrollWidth) * 100) + '%';
        prevBtn.disabled = viewport.scrollLeft <= 1;
        nextBtn.disabled = viewport.scrollLeft >= max - 1;
      };
      prevBtn.addEventListener('click', () => viewport.scrollBy({ left: -viewport.clientWidth, behavior: 'smooth' }));
      nextBtn.addEventListener('click', () => viewport.scrollBy({ left: viewport.clientWidth, behavior: 'smooth' }));
      viewport.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync);
      sync();
    }

    /* Live "Show (N)" count : re-render the section for the pending filter params */
    _initLiveCount(form) {
      if (!form) return;
      const countEl = this.querySelector('[data-filter-count]');
      const sectionId = this.dataset.sectionId;
      const baseUrl = this.dataset.collectionUrl || window.location.pathname;
      if (!countEl || !sectionId) return;
      let controller = null;
      form.addEventListener('change', async (e) => {
        if (!e.target.matches('[data-filter-input]')) return;
        if (controller) controller.abort();
        controller = new AbortController();
        try {
          const params = this._params(form);
          params.set('section_id', sectionId);
          const res = await fetch(`${baseUrl}?${params.toString()}`, { signal: controller.signal });
          if (!res.ok) return;
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          const fresh = doc.querySelector('[data-product-count]');
          if (fresh) countEl.textContent = fresh.textContent.trim();
        } catch (err) {
          /* aborted or offline - keep the previous count */
        }
      });
    }

    _params(form) {
      const params = new URLSearchParams();
      form.querySelectorAll('input[type="checkbox"][data-filter-input]:checked').forEach((cb) => {
        params.append(cb.name, cb.value);
      });
      form.querySelectorAll('input[type="number"][data-filter-input]').forEach((n) => {
        if (n.value) params.append(n.name, n.value);
      });
      return params;
    }
    /* Pin the overlay flush under the collection title-bar ; once the page is
       scrolled past it, clamp to the sticky facets bar instead. */
    _syncTop() {
      const bar = document.querySelector('.pm-collection__title-bar');
      const controls = document.querySelector('.pm-collection__controls');
      let top = 0;
      if (bar) top = bar.getBoundingClientRect().bottom;
      if (controls) top = Math.max(top, controls.getBoundingClientRect().bottom);
      if (top > 0) this.style.setProperty('--pm-overlay-top', Math.round(top) + 'px');
    }
    open() {
      if (this._sort) this._sort.open = false; // opening Filters closes the Sort dropdown
      this._syncTop();
      this._onReposition = this._onReposition || (() => this._syncTop());
      window.addEventListener('scroll', this._onReposition, { passive: true });
      window.addEventListener('resize', this._onReposition);
      this.removeAttribute('hidden');
      requestAnimationFrame(() => { this.dataset.open = 'true'; });
      if (this._trigger) this._trigger.setAttribute('aria-expanded', 'true');
    }
    close() {
      delete this.dataset.open;
      if (this._onReposition) {
        window.removeEventListener('scroll', this._onReposition);
        window.removeEventListener('resize', this._onReposition);
      }
      if (this._trigger) this._trigger.setAttribute('aria-expanded', 'false');
      setTimeout(() => { if (this.dataset.open !== 'true') this.setAttribute('hidden', ''); }, 300);
    }
    toggle() { this.dataset.open === 'true' ? this.close() : this.open(); }
    _onKey(e) {
      if (e.key === 'Escape' && this.dataset.open === 'true') this.close();
    }
    _onSubmit(e) {
      e.preventDefault();
      const params = this._params(e.target);
      const url = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
      window.location.href = url;
    }
  }
  customElements.define('pm-filter-overlay', PmFilterOverlay);

  /* <pm-filter-bar> - the facets bar must stay visible at the top of the
     browser at all times (client spec). position:sticky can't escape its
     title-bar parent, so once the title-bar scrolls past the header we
     switch the bar to position:fixed under the header. */
  customElements.define('pm-filter-bar', class extends HTMLElement {
    connectedCallback() {
      this._bar = this.closest('.pm-collection__title-bar');
      if (!this._bar) return;
      this._onScroll = () => this._sync();
      window.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onScroll);
      this._sync();
    }
    disconnectedCallback() {
      if (!this._bar) return;
      window.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onScroll);
    }
    _sync() {
      const headerH = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-h')) || 60;
      const shouldFix = this._bar.getBoundingClientRect().bottom <= headerH + this.offsetHeight;
      this.classList.toggle('is-fixed', shouldFix);
    }
  });

  /* ---------------------------------------------------------------------------
   * <pm-infinite-grid> - listing that pulls its own next page
   *   Wraps the collection grid and the search results grid. The next page is
   *   fetched when the sentinel closing the grid nears the viewport, and its
   *   cards are appended in place of the sentinel.
   *
   *   It also fills the FIRST page. Detection mode collapses a frame's whole
   *   family into one card, so a page of raw results can render two or three
   *   cards - a search on a model name is the extreme case, its family taking
   *   the entire first page. The sentinel then sits inside the viewport from
   *   the start, the observer fires immediately, and it re-arms after each
   *   append until the grid is long enough to push it out of view or the
   *   listing runs out of pages. Numbered pagination cannot do this : the cut
   *   happens on the raw results, before a single card is collapsed.
   *
   *   A custom element rather than a one-off IIFE : the search page re-renders
   *   its grid through facets.js on every filter change, which would leave a
   *   plain listener observing a detached sentinel.
   * --------------------------------------------------------------------------*/
  customElements.define('pm-infinite-grid', class extends HTMLElement {
    connectedCallback() {
      if (this._io) return;
      this._syncCount();
      /* facets.js swaps the grid in first and the counters straight after, so on
         a filter change the line above is overwritten by the raw count a tick
         later : re-apply once the whole swap has landed. */
      requestAnimationFrame(() => this._syncCount());
      const sentinel = this.querySelector('[data-infinite-sentinel]');
      /* No sentinel : the listing already holds everything, so the empty state
         is decided here and now rather than by _stop(). */
      if (!sentinel || !('IntersectionObserver' in window)) { this._revealEmpty(); return; }
      this._loading = false;
      this._io = new IntersectionObserver((entries) => {
        if (entries.some((en) => en.isIntersecting)) this._loadNext(sentinel);
      }, { rootMargin: '600px 0px' });
      this._io.observe(sentinel);
    }

    /* Cards actually on screen, not search.results_count : the search grid drops
       the semantic filler the engine pads its answer with, and detection mode
       collapses a whole family into one card, so the raw count says nothing
       about what the visitor sees. Rewrites the counters facets.liquid prints. */
    _syncCount() {
      const one = this.dataset.countOne;
      const other = this.dataset.countOther;
      if (!one || !other) return;
      const n = this.querySelectorAll('.pm-card, .pm-search__result').length;
      /* The two templates are the translation rendered at count 1 and at count
         999 : swap the number back out, digit groups included so a thousands
         separator goes whole and the trailing space survives. */
      const label = (n === 1 ? one : other).replace(/\d+(?:[\s\u00a0\u202f.,]\d+)*/, String(n));
      document.querySelectorAll('#ProductCount, #ProductCountDesktop').forEach((el) => {
        el.textContent = label;
      });
    }

    /* The listing is exhausted : with nothing kept, say so. The server cannot
       decide this on page 1 - under a price sort the genuine matches are
       scattered across the raw pages. */
    _revealEmpty() {
      const empty = this.querySelector('[data-search-empty]');
      if (!empty) return;
      const isEmpty = this.querySelectorAll('.pm-card, .pm-search__result').length === 0;
      empty.classList.toggle('hidden', !isEmpty);
    }

    disconnectedCallback() {
      if (this._io) this._io.disconnect();
      this._io = null;
    }

    _stop(sentinel) {
      if (this._io) this._io.disconnect();
      this._io = null;
      sentinel.remove();
      this._revealEmpty();
    }

    async _loadNext(sentinel) {
      if (this._loading || !this._io) return;
      const next = sentinel.dataset.nextUrl;
      if (!next) { this._stop(sentinel); return; }
      this._loading = true;
      try {
        const res = await fetch(next);
        if (!res.ok) throw new Error(res.status);
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const newGrid = doc.querySelector('[data-product-grid]');
        const newSentinel = newGrid && newGrid.querySelector('[data-infinite-sentinel]');
        if (newGrid) {
          [...newGrid.children].forEach((child) => {
            if (child.hasAttribute('data-infinite-sentinel')) return;
            // Every page carries its own empty-state node - keep the first one only.
            if (child.hasAttribute('data-search-empty')) return;
            // Detection mode : a family straddling two pages would repeat -
            // skip cards whose data-family-key is already in the grid.
            const famKey = child.dataset ? child.dataset.familyKey : null;
            if (famKey && this.querySelector('[data-family-key="' + CSS.escape(famKey) + '"]')) return;
            this.insertBefore(child, sentinel);
          });
        }
        if (newSentinel && newSentinel.dataset.nextUrl) {
          sentinel.dataset.nextUrl = newSentinel.dataset.nextUrl;
        } else {
          this._stop(sentinel);
        }
      } catch (e) {
        /* network hiccup : keep the sentinel so the next intersection retries */
      }
      this._loading = false;
      this._syncCount();
      if (this._io && sentinel.isConnected) {
        /* re-arm : if the sentinel is still in view after appending, fire again -
           this is what keeps filling a page that collapsed to a couple of cards */
        this._io.unobserve(sentinel);
        this._io.observe(sentinel);
      }
    }
  });

  /* ---------------------------------------------------------------------------
   * In-view videos - opt in with [data-inview-videos] on any container
   *   Autoplaying videos inside that container only run while they are visible :
   *   leaving the viewport - or being scrolled out of a carousel track, which
   *   clips them the same way - pauses them, coming back plays them again.
   *   pause() also clears the browser's own autoplay flag, so a video that has
   *   never been on screen never starts at all.
   *   Only the videos WE paused are resumed : one the visitor paused by hand on
   *   its controls stays paused.
   * --------------------------------------------------------------------------*/
  (() => {
    if (!('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const video = entry.target;
        if (entry.isIntersecting) {
          if (!video._pmAutoPaused) return;
          video._pmAutoPaused = false;
          const played = video.play();
          if (played && played.catch) played.catch(() => {});
        } else if (!video.paused || !video.played.length) {
          video._pmAutoPaused = true;
          video.pause();
        }
      });
    }, { threshold: 0.01 });

    const observe = (root) => {
      root.querySelectorAll('[data-inview-videos] video[autoplay]').forEach((video) => {
        if (video._pmInview) return;
        video._pmInview = true;
        io.observe(video);
      });
    };
    observe(document);
    // Theme editor : a re-rendered section brings brand new <video> nodes
    document.addEventListener('shopify:section:load', (e) => observe(e.target));
  })();

})();
